import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { subscribeToTenant } from '../../conversations/services/conversationHub';
import {
  answerCall,
  CallSession,
  CallSessionStatus,
  getActiveCallSessions,
  getCallCounterpartName,
  getCallingCapabilities,
  hangupCall,
  holdCall,
  isActiveCallStatus,
  normalizeCallSession,
  resumeCall,
  transferCall,
} from '../services/callingService';
import styles from './CallCenter.module.css';

const statusKeys: Record<CallSessionStatus, string> = {
  initiating: 'statusInitiating',
  ringing: 'statusRinging',
  inProgress: 'statusInProgress',
  onHold: 'statusOnHold',
  ended: 'statusEnded',
  failed: 'statusFailed',
  cancelled: 'statusCancelled',
};

const isInbound = (session: CallSession) => session.direction.toLowerCase() === 'inbound';

const mergeSessions = (sessions: CallSession[], incoming: CallSession): CallSession[] => {
  const withoutIncoming = sessions.filter((session) => session.id !== incoming.id);
  if (!isActiveCallStatus(incoming.status)) {
    return withoutIncoming;
  }

  return [incoming, ...withoutIncoming];
};

/**
 * Audio stays on the 3CX client; this is the CRM-side view of the call plus the control commands.
 */
export const CallCenter: React.FC = () => {
  const { t } = useTranslation('calling');
  const { auth, hasPermission } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const tenantId = auth?.tenantId;
  const canSeeCalls = hasPermission(PermissionScopes.conversationsRead);

  const [sessions, setSessions] = useState<CallSession[]>([]);
  const [callControlEnabled, setCallControlEnabled] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [transferForId, setTransferForId] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!tenantId || !canSeeCalls) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const [capabilities, active] = await Promise.all([
          getCallingCapabilities(),
          getActiveCallSessions(),
        ]);
        if (cancelled) {
          return;
        }

        setCallControlEnabled(capabilities.callControlEnabled);
        setSessions(active.filter((session) => isActiveCallStatus(session.status)));
      } catch {
        // A missing voice connector is the normal case; stay silent.
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, canSeeCalls]);

  useEffect(() => {
    if (!tenantId || !canSeeCalls) {
      return;
    }

    let unsubscribe: (() => Promise<void>) | null = null;
    let cancelled = false;

    const subscribe = async () => {
      try {
        const stop = await subscribeToTenant({
          tenantId,
          onCallSessionUpdated: (evt) => {
            const session = normalizeCallSession(evt.session);
            if (!session.id) {
              return;
            }

            setSessions((current) => mergeSessions(current, session));
          },
        });

        if (cancelled) {
          await stop();
          return;
        }

        unsubscribe = stop;
      } catch {
        // Realtime is best effort here; the polling-free fallback is the next page load.
      }
    };

    void subscribe();
    return () => {
      cancelled = true;
      void unsubscribe?.();
    };
  }, [tenantId, canSeeCalls]);

  const ringingInbound = useMemo(
    () => sessions.find((session) => isInbound(session) && session.status === 'ringing' && !dismissedIds.includes(session.id)),
    [sessions, dismissedIds],
  );

  const activeCall = useMemo(
    () => sessions.find((session) => session.id !== ringingInbound?.id),
    [sessions, ringingInbound],
  );

  const runCommand = useCallback(async (
    session: CallSession,
    command: (id: string) => Promise<CallSession>,
    requiresCallControl = true,
  ) => {
    if (requiresCallControl && !callControlEnabled) {
      showToast(t('callControlUnavailable'), 'info');
      return;
    }

    setBusyId(session.id);
    try {
      const updated = await command(session.id);
      if (mountedRef.current) {
        setSessions((current) => mergeSessions(current, updated));
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('callControlUnavailable'), 'error');
    } finally {
      if (mountedRef.current) {
        setBusyId(null);
      }
    }
  }, [callControlEnabled, showToast, t]);

  const openConversation = (session: CallSession) => {
    setDismissedIds((current) => (current.includes(session.id) ? current : [...current, session.id]));
    if (session.conversationId) {
      navigate(`/conversations/${session.conversationId}`);
    } else if (session.contactId) {
      navigate(`/contacts/${session.contactId}`);
    }
  };

  const submitTransfer = async (session: CallSession) => {
    const target = transferTarget.trim();
    if (!target) {
      return;
    }

    await runCommand(session, (id) => transferCall(id, target));
    setTransferForId(null);
    setTransferTarget('');
  };

  if (!tenantId || !canSeeCalls) {
    return null;
  }

  const callerLabel = (session: CallSession) =>
    getCallCounterpartName(session)?.trim()
    || session.contactDisplayNumber?.trim()
    || (isInbound(session) ? session.fromNumber : session.toNumber)
    || t('unknownCaller');

  return (
    <>
      {ringingInbound && (
        <aside className={styles.overlay} role="alert" aria-live="assertive">
          <div className={styles.overlayHeader}>
            <span className={styles.pulse} aria-hidden="true" />
            {t('incomingCall')}
          </div>
          <div className={styles.callerName}>{callerLabel(ringingInbound)}</div>
          <div className={styles.callerNumber}>{ringingInbound.fromNumber}</div>
          <div className={styles.overlayActions}>
            {callControlEnabled && (
              <button
                type="button"
                className={`${styles.button} ${styles.primary}`}
                disabled={busyId === ringingInbound.id}
                onClick={() => void runCommand(ringingInbound, answerCall)}
              >
                {t('answer')}
              </button>
            )}
            <button
              type="button"
              className={`${styles.button} ${styles.subtle}`}
              onClick={() => openConversation(ringingInbound)}
            >
              {t('openConversation')}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.subtle}`}
              onClick={() => setDismissedIds((current) => [...current, ringingInbound.id])}
            >
              {t('dismiss')}
            </button>
          </div>
          <p className={styles.hint}>{callControlEnabled ? t('answerHint') : t('callControlUnavailable')}</p>
        </aside>
      )}

      {activeCall && (
        <div className={styles.bar} role="status">
          <div className={styles.barInfo}>
            <span className={styles.barTitle}>{callerLabel(activeCall)}</span>
            <span className={styles.barMeta}>
              {`${isInbound(activeCall) ? t('inbound') : t('outbound')} · ${t(statusKeys[activeCall.status])}`}
            </span>
          </div>
          <div className={styles.barActions}>
            {activeCall.conversationId && (
              <button
                type="button"
                className={styles.button}
                onClick={() => navigate(`/conversations/${activeCall.conversationId}`)}
              >
                {t('openConversation')}
              </button>
            )}
            {callControlEnabled && activeCall.status === 'onHold' && (
              <button
                type="button"
                className={styles.button}
                disabled={busyId === activeCall.id}
                onClick={() => void runCommand(activeCall, resumeCall)}
              >
                {t('resume')}
              </button>
            )}
            {callControlEnabled && activeCall.status !== 'onHold' && (
              <button
                type="button"
                className={styles.button}
                disabled={busyId === activeCall.id}
                onClick={() => void runCommand(activeCall, holdCall)}
              >
                {t('hold')}
              </button>
            )}
            {callControlEnabled && transferForId !== activeCall.id && (
              <button
                type="button"
                className={styles.button}
                disabled={busyId === activeCall.id}
                onClick={() => setTransferForId(activeCall.id)}
              >
                {t('transfer')}
              </button>
            )}
            {callControlEnabled && transferForId === activeCall.id && (
              <div className={styles.transferRow}>
                <input
                  className={styles.transferInput}
                  value={transferTarget}
                  autoFocus
                  placeholder={t('transferTarget')}
                  onChange={(event) => setTransferTarget(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      void submitTransfer(activeCall);
                    }
                    if (event.key === 'Escape') {
                      setTransferForId(null);
                    }
                  }}
                />
                <button
                  type="button"
                  className={styles.button}
                  disabled={busyId === activeCall.id || !transferTarget.trim()}
                  onClick={() => void submitTransfer(activeCall)}
                >
                  {t('transferConfirm')}
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setTransferForId(null)}
                >
                  {t('cancel')}
                </button>
              </div>
            )}
            {/* Ending the call always closes the CRM record, even without Call Control. */}
            <button
              type="button"
              className={`${styles.button} ${styles.danger}`}
              disabled={busyId === activeCall.id}
              onClick={() => void runCommand(activeCall, hangupCall, false)}
            >
              {t('hangup')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CallCenter;
